"use client";

import { useSyncExternalStore } from "react";
import { UAParser } from "my-ua-parser";

export interface UseIsMobileResult {
  /** Viewport is small (<=940px). Use for layout: compact vs desktop UI. */
  isSmallScreen: boolean;
  /** Actual phone/tablet device. Use for phone-specific features (tracking, stay-off-phone). */
  isMobileDevice: boolean;
  /** Alias for isSmallScreen. Use for layout decisions (backward compat). */
  isMobile: boolean;
  /** True after the first client-side detection has run. Use to avoid redirect loops. */
  hasResolved: boolean;
}

const SMALL_SCREEN_BREAKPOINT = 940;

const SERVER_SNAPSHOT: UseIsMobileResult = {
  isSmallScreen: false,
  isMobileDevice: false,
  isMobile: false,
  hasResolved: false,
};

interface DeviceInfo {
  isMobileDevice: boolean;
  isMobileOS: boolean;
}

/*
 * All components share one window resize listener and one snapshot object.
 * A resize listener per component instance meant every resize event ran a
 * setState per subscriber with a React flush between them — a render pass per
 * component (with ~14 consumers, several wrapping the dashboard). The shared
 * listener notifies all subscribers in one task so React batches them into a
 * single render pass, and the cached snapshot means no render at all unless a
 * value actually flipped.
 */
let deviceInfo: DeviceInfo | null = null;
let cachedSnapshot: UseIsMobileResult | null = null;
const subscribers = new Set<() => void>();
let resizeListenerAttached = false;
/** Pending rAF handle, so a burst of resize events notifies once. */
let pendingFrame = 0;

function getDeviceInfo(): DeviceInfo {
  if (!deviceInfo) {
    const parser = new UAParser();
    const device = parser.getDevice();
    const os = parser.getOS();
    deviceInfo = {
      isMobileDevice: device.type === "mobile" || device.type === "tablet",
      isMobileOS: os.name === "iOS" || os.name === "Android",
    };
  }
  return deviceInfo;
}

function getSnapshot(): UseIsMobileResult {
  const { isMobileDevice, isMobileOS } = getDeviceInfo();
  const isSmallScreen = window.innerWidth <= SMALL_SCREEN_BREAKPOINT;
  // Mobile device: actual phone/tablet, or mobile OS on small viewport
  const effectiveMobileDevice = isMobileDevice || (isMobileOS && isSmallScreen);

  if (
    cachedSnapshot &&
    cachedSnapshot.isSmallScreen === isSmallScreen &&
    cachedSnapshot.isMobileDevice === effectiveMobileDevice
  ) {
    return cachedSnapshot;
  }

  cachedSnapshot = {
    isSmallScreen,
    isMobileDevice: effectiveMobileDevice,
    isMobile: isSmallScreen,
    hasResolved: true,
  };
  return cachedSnapshot;
}

function notifySubscribers(): void {
  pendingFrame = 0;
  for (const notify of subscribers) {
    notify();
  }
}

/*
 * Dragging, snapping, or restoring a window fires a burst of resize events —
 * dozens, not one. Notifying on every single event meant a burst that crossed
 * the breakpoint ran the full cascade once per event: useSyncExternalStore
 * updates in React's synchronous lane (it must, to avoid tearing), so every
 * one of the ~17 useIsMobile consumers re-rendered *inside* the resize event
 * itself. Measured with a LoAF observer against a resize burst, that is what
 * Sentry attributes to `event-listener DOMWindow.onresize` — the time is in
 * the render the listener triggers, not in the listener body, which is why
 * timing the callbacks alone shows nothing.
 *
 * Coalescing to one notification per animation frame collapses the burst into
 * a single cascade. Nothing else changes: the frame lands within ~16ms, so the
 * layout still flips live, and the cached snapshot still means a resize that
 * does not cross the breakpoint re-renders nobody.
 */
function scheduleNotify(): void {
  if (pendingFrame !== 0) {
    return;
  }
  if (typeof requestAnimationFrame !== "function") {
    notifySubscribers();
    return;
  }
  pendingFrame = requestAnimationFrame(notifySubscribers);
}

function subscribe(onStoreChange: () => void): () => void {
  if (!resizeListenerAttached) {
    window.addEventListener("resize", scheduleNotify);
    resizeListenerAttached = true;
  }
  subscribers.add(onStoreChange);
  return () => {
    subscribers.delete(onStoreChange);
  };
}

/**
 * Detects viewport size and device type for layout and feature decisions.
 * - isSmallScreen: viewport <= 940px → use compact layout (no redirect)
 * - isMobileDevice: actual phone/tablet → redirect to /mobile, enable phone tracking
 * Small desktops get compact layout but NOT phone tracking or redirect.
 */
export function useIsMobile(): UseIsMobileResult {
  return useSyncExternalStore(subscribe, getSnapshot, () => SERVER_SNAPSHOT);
}
